import {
  Phone,
  Mail,
  MapPin,
  Globe,
  Linkedin,
  FileText,
  Calendar,
  Download,
  ArrowRight,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import type { VCard } from "../../types/vcard";
import SaveContact from "./SaveContact";
import "./BusinessPublicCard.css";

interface BusinessPublicCardProps {
  vcard: VCard;
}

type BusinessData = {
  company?: string;
  title?: string;
  department?: string;
  linkedin?: string;
  logoUrl?: string;
  tagline?: string;
  brochureUrl?: string;
  digitalCalendar?: {
    type?: string;
    url?: string;
  };
};

// Map common country names / ISO codes → dial prefix
const COUNTRY_DIAL: Record<string, string> = {
  australia: "+61",
  au: "+61",
  "united states": "+1",
  us: "+1",
  usa: "+1",
  canada: "+1",
  ca: "+1",
  "united kingdom": "+44",
  uk: "+44",
  gb: "+44",
  "new zealand": "+64",
  nz: "+64",
  china: "+86",
  cn: "+86",
  japan: "+81",
  jp: "+81",
  india: "+91",
  in: "+91",
  germany: "+49",
  de: "+49",
  france: "+33",
  fr: "+33",
};

// Two-digit dial prefixes (order matters — check longer prefixes first)
const TWO_DIGIT_PREFIXES = new Set([
  "20",
  "27",
  "30",
  "31",
  "32",
  "33",
  "34",
  "36",
  "39",
  "40",
  "41",
  "43",
  "44",
  "45",
  "46",
  "47",
  "48",
  "49",
  "51",
  "52",
  "53",
  "54",
  "55",
  "56",
  "57",
  "58",
  "60",
  "61",
  "62",
  "63",
  "64",
  "65",
  "66",
  "81",
  "82",
  "84",
  "86",
  "90",
  "91",
  "92",
  "93",
  "94",
  "95",
  "98",
]);

const formatPhoneNumber = (num: string): string => {
  if (!num) return num;
  const cleaned = num.replace(/[^\d+]/g, "");
  if (!cleaned.startsWith("+")) return cleaned;

  const digits = cleaned.slice(1); // strip leading +

  // Determine country code length (1 or 2 digits; 3-digit codes are rare)
  let ccLen = 1;
  if (digits.length >= 2 && TWO_DIGIT_PREFIXES.has(digits.slice(0, 2)))
    ccLen = 2;

  const cc = digits.slice(0, ccLen);
  const local = digits.slice(ccLen);

  // Group local digits in blocks of 3
  const groups = local.match(/.{1,3}/g) ?? [];
  return `+${cc} ${groups.join(" ")}`.trim();
};

const ensureCountryCode = (num: string, country?: string): string => {
  if (!num) return num;
  // Already has a dial code
  if (num.startsWith("+") || num.startsWith("00")) return num;
  // Try to infer from vcard address country
  const key = (country || "").toLowerCase().trim();
  const dialCode = COUNTRY_DIAL[key];
  if (dialCode) {
    // Strip leading zeros before attaching the dial code
    return `${dialCode}${num.replace(/^0+/, "")}`;
  }
  return num;
};

const getBusinessData = (vcard: VCard): BusinessData => {
  const info = vcard.categorySpecificInfo;
  if (!info || typeof info !== "object") return {};
  const business = (info as Record<string, unknown>).business;
  if (!business || typeof business !== "object") return {};
  return business as BusinessData;
};

const BusinessPublicCard = ({ vcard }: BusinessPublicCardProps) => {
  const businessData = getBusinessData(vcard);

  const displayName =
    `${vcard.firstName || ""} ${vcard.lastName || ""}`.trim() ||
    "TAGiFY-iT Owner";
  const jobTitle = businessData.title || vcard.jobTitle || "";
  const company = businessData.company || vcard.company || "";
  const linkedin = businessData.linkedin || "";
  const logoUrl = businessData.logoUrl || "";
  const tagline = businessData.tagline || "";
  const brochureUrl = businessData.brochureUrl || "";
  const calendarUrl = businessData.digitalCalendar?.url || "";
  const rawPhone = vcard.mobilePhone || vcard.workPhone || vcard.phone || "";
  const phone = formatPhoneNumber(
    ensureCountryCode(rawPhone, vcard.address?.country),
  );
  const email = vcard.workEmail || vcard.email || "";
  const website = vcard.website || "";
  const location = [vcard.address?.city, vcard.address?.state]
    .filter(Boolean)
    .join(", ");
  const photoUrl =
    typeof vcard.photo === "string" && vcard.photo.length > 0
      ? vcard.photo
      : null;
  const initials = `${vcard.firstName?.[0] || ""}${vcard.lastName?.[0] || ""}`;
  const pageUrl = window.location.href;

  const hasShareLinks = website || linkedin || brochureUrl || calendarUrl;
  const hasCompanyCard = company || logoUrl || tagline || website;

  return (
    // Page backdrop — card sits centred on top
    <div className="bpc-page">
      <div className="bpc-outer-card">
        {/* ── Red header (rounded top corners from outer card) ── */}
        <div className="bpc-header">
          <div className="relative px-5 pt-8 pb-8">
            {/* Social icon strip – top right */}
            <div className="absolute top-4 right-4 flex gap-2">
              {linkedin && (
                <a
                  href={linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bpc-social-btn p-2 rounded-xl text-white transition-opacity hover:opacity-75"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="bpc-social-btn p-2 rounded-xl text-white transition-opacity hover:opacity-75"
                  aria-label="Email"
                >
                  <Mail className="h-5 w-5" />
                </a>
              )}
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bpc-social-btn p-2 rounded-xl text-white transition-opacity hover:opacity-75"
                  aria-label="Website"
                >
                  <Globe className="h-5 w-5" />
                </a>
              )}
            </div>

            {/* Profile row */}
            <div className="flex items-center gap-5 mt-2">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={displayName}
                  className="bpc-photo w-24 h-24 rounded-full object-cover shrink-0"
                />
              ) : (
                <div className="bpc-photo w-24 h-24 rounded-full bg-white flex items-center justify-center shrink-0">
                  <span className="text-3xl font-bold text-red-600">
                    {initials || "TG"}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-white leading-tight">
                  {displayName}
                </h1>
                {jobTitle && (
                  <p className="text-red-100 text-sm mt-0.5">{jobTitle}</p>
                )}
                {company && (
                  <p className="text-white font-semibold mt-0.5">{company}</p>
                )}
              </div>
            </div>

            {/* TAGiFY-iT branding — remove with premium */}
            <a
              href="/membership"
              className="absolute bottom-3 right-4 flex items-center gap-1.5 opacity-70 hover:opacity-100 transition-opacity"
              title="Remove this badge with a Premium subscription"
            >
              <img
                src="/TAGiF-iT_Color_SVG.svg"
                alt="TAGiFY-iT"
                className="h-6 w-auto"
              />
              <span className="text-white text-[10px] font-medium tracking-wide">
                TAGiFY-iT
              </span>
            </a>
          </div>
        </div>

        {/* ── Inner card body (the #e8ecf1 area) ── */}
        <div className="px-4 pt-4 pb-6 space-y-4">
          {/* Contact info */}
          <div className="bpc-card rounded-3xl pl-8 pr-5 py-5 space-y-4">
            {phone && (
              <div className="flex items-center gap-4">
                <Phone className="h-5 w-5 text-red-600 shrink-0" />
                <a
                  href={`tel:${phone}`}
                  className="text-gray-700 font-medium hover:text-red-600 transition-colors"
                >
                  {phone}
                </a>
              </div>
            )}
            {phone && email && <div className="bpc-divider" />}
            {email && (
              <div className="flex items-center gap-4">
                <Mail className="h-5 w-5 text-red-600 shrink-0" />
                <a
                  href={`mailto:${email}`}
                  className="text-blue-600 hover:underline truncate"
                >
                  {email}
                </a>
              </div>
            )}
            {email && location && <div className="bpc-divider" />}
            {location && (
              <div className="flex items-center gap-4">
                <MapPin className="h-5 w-5 text-red-600 shrink-0" />
                <span className="text-gray-700">{location}</span>
              </div>
            )}
          </div>

          {/* Save Contact */}
          <SaveContact
            vcard={vcard}
            label="SAVE CONTACT"
            className="w-full flex items-center justify-center gap-2 bg-red-600 text-white py-4 rounded-full font-bold text-lg tracking-widest hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          />

          {/* Quick-action row */}
          <div className="flex gap-3">
            {phone && (
              <a
                href={`tel:${phone}`}
                className="bpc-btn-neu flex-1 flex items-center justify-center gap-2 rounded-2xl py-3 text-gray-600 font-medium text-sm hover:text-red-600 transition-colors"
              >
                <Phone className="h-4 w-4 text-red-600" />
                Call
              </a>
            )}
            {email && (
              <a
                href={`mailto:${email}`}
                className="bpc-btn-neu flex-1 flex items-center justify-center gap-2 rounded-2xl py-3 text-gray-600 font-medium text-sm hover:text-red-600 transition-colors"
              >
                <Mail className="h-4 w-4 text-red-600" />
                Email
              </a>
            )}
            {linkedin && (
              <a
                href={linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="bpc-btn-neu flex-1 flex items-center justify-center gap-2 rounded-2xl py-3 text-gray-600 font-medium text-sm hover:text-[#0A66C2] transition-colors"
              >
                <span className="bg-[#0A66C2] rounded-[4px] p-0.5 flex items-center justify-center shrink-0">
                  <Linkedin className="h-3.5 w-3.5 text-white" />
                </span>
                LinkedIn
              </a>
            )}
          </div>

          {/* Company card */}
          {hasCompanyCard && (
            <div className="bpc-card rounded-3xl p-5">
              <div className="flex items-center gap-3 mb-4">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={company}
                    className="h-14 w-14 object-contain rounded-xl"
                  />
                ) : (
                  <div className="bpc-logo-placeholder h-14 w-14 rounded-xl flex items-center justify-center shrink-0">
                    <span className="text-red-600 font-black text-2xl">
                      {company?.[0]?.toUpperCase() || "C"}
                    </span>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-black text-gray-900 uppercase tracking-wide truncate">
                    {company}
                  </p>
                  {tagline && (
                    <p className="text-sm text-gray-500">{tagline}</p>
                  )}
                </div>
              </div>
              <div className="bpc-divider mb-4" />
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between text-red-600 font-medium hover:opacity-75 transition-opacity"
                >
                  <span className="flex items-center gap-1.5 truncate">
                    <span className="truncate">
                      {website.replace(/^https?:\/\//, "")}
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0" />
                  </span>
                  <span className="ml-2 shrink-0 text-gray-400">›</span>
                </a>
              )}
            </div>
          )}

          {/* Share Contact */}
          {hasShareLinks && (
            <div className="bpc-card rounded-3xl p-5">
              <div className="flex items-start gap-4">
                {/* Left: title + 2×2 link grid */}
                <div className="flex-1 min-w-0">
                  <h2 className="font-bold text-gray-900 mb-3">
                    Share Contact
                  </h2>
                  <div className="bpc-divider mb-3" />
                  <div className="space-y-3">
                    {/* Row 1: Website + LinkedIn */}
                    <div className="grid grid-cols-2 gap-x-2">
                      {website && (
                        <a
                          href={website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                        >
                          <span className="bpc-share-chip p-1.5 rounded-lg shrink-0">
                            <Globe className="h-3.5 w-3.5 text-red-600" />
                          </span>
                          Website
                        </a>
                      )}
                      {linkedin && (
                        <a
                          href={linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                        >
                          <span className="bg-[#0A66C2] rounded-[4px] p-1 flex items-center justify-center shrink-0">
                            <Linkedin className="h-3 w-3 text-white" />
                          </span>
                          LinkedIn
                        </a>
                      )}
                    </div>

                    {/* Row 2: Brochure + Book Meeting */}
                    <div className="grid grid-cols-2 gap-x-2">
                      {brochureUrl ? (
                        <a
                          href={brochureUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                        >
                          <span className="bpc-share-chip p-1.5 rounded-lg shrink-0">
                            <FileText className="h-3.5 w-3.5 text-red-600" />
                          </span>
                          Brochure
                        </a>
                      ) : (
                        <span className="flex items-center gap-2 text-sm text-gray-300">
                          <span className="bpc-share-chip p-1.5 rounded-lg shrink-0">
                            <FileText className="h-3.5 w-3.5 text-red-200" />
                          </span>
                          Brochure
                        </span>
                      )}
                      {calendarUrl ? (
                        <a
                          href={calendarUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors whitespace-nowrap"
                        >
                          <span className="bpc-share-chip p-1.5 rounded-lg shrink-0">
                            <Calendar className="h-3.5 w-3.5 text-red-600" />
                          </span>
                          Book Meeting
                        </a>
                      ) : (
                        <span className="flex items-center gap-2 text-sm text-gray-300 whitespace-nowrap">
                          <span className="bpc-share-chip p-1.5 rounded-lg shrink-0">
                            <Calendar className="h-3.5 w-3.5 text-red-200" />
                          </span>
                          Book Meeting
                        </span>
                      )}
                    </div>

                    <div className="bpc-divider" />
                  </div>
                </div>

                {/* Right: QR code spanning full height */}
                <div className="bpc-qr-frame p-2 rounded-xl shrink-0 self-center">
                  <QRCodeSVG value={pageUrl} size={54} level="M" />
                </div>
              </div>
            </div>
          )}

          {/* Download App */}
          <a
            href="https://tagify-it.com"
            target="_blank"
            rel="noopener noreferrer"
            className="bpc-btn-neu flex items-center justify-between rounded-2xl px-5 py-4 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Download className="h-5 w-5 text-red-600 shrink-0" />
            <div className="flex items-center gap-2">
              <img
                src="/TAGiF-iT_Color_SVG.svg"
                alt="TAGiFY-iT"
                className="h-5 w-auto"
              />
              <span className="font-medium">Download TAGiFY-iT App</span>
            </div>
            <span className="text-gray-400 text-lg shrink-0">›</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default BusinessPublicCard;
